import React, { useState, useEffect, useCallback } from "react";
import { Bell, Send, CheckCircle, AlertCircle, Wifi, Bot, ToggleLeft, ToggleRight } from "lucide-react";
import { getAuthHeaders, handleResponse } from "shared/api/function_data";
import { CollapsibleSection, FormField } from "./AdminShared";

const BASE_URL = "https://1ftxw98ih3.execute-api.eu-west-1.amazonaws.com/cia_pulse_1";

const EVENT_OPTIONS = [
  { key: "business_update", label: "Business Update Published", icon: "📊", desc: "When someone saves business updates" },
  { key: "action_created", label: "Action Item Created", icon: "🆕", desc: "When a new action item is added" },
  { key: "action_status_change", label: "Action Status Changed", icon: "🔄", desc: "When an action item's status changes" },
  { key: "action_ecd_change", label: "Action ECD Changed", icon: "📅", desc: "When an action item's deadline date is updated" },
  { key: "action_comment_added", label: "Action Comment Added", icon: "💬", desc: "When someone adds a comment on an action item" },
  { key: "report_uploaded", label: "Report PDF Uploaded", icon: "📄", desc: "When a PDF report is uploaded" },
  { key: "callout_updated", label: "Callout Updated", icon: "📌", desc: "When report callouts are saved" },
  { key: "question_posted", label: "Question Posted", icon: "❓", desc: "When someone asks a question on a report" },
  { key: "answer_posted", label: "Answer Posted", icon: "💬", desc: "When someone answers a question" },
  { key: "action_overdue", label: "Action Item Overdue", icon: "🔴", desc: "When an action passes its ECD (daily scan)" },
  { key: "action_expiring_soon", label: "Action Expiring Soon", icon: "⏰", desc: "Warning before ECD deadline (daily scan)" },
];

const DEFAULT_CONFIG = {
  enabled: false,
  mode: "webhook",
  webhook_url: "",
  bot_channel_id: "",
  events: {
    business_update: true,
    action_created: true,
    action_status_change: true,
    action_ecd_change: true,
    action_comment_added: true,
    report_uploaded: true,
    callout_updated: true,
    question_posted: true,
    answer_posted: true,
    action_overdue: true,
    action_expiring_soon: true,
  },
  overdue_settings: {
    warn_days_before: 2,
    notify_once_per_item: true,
  },
};

function NotificationsTab({ functionId, year }) {
  const [config, setConfig] = useState(null);
  const [origConfig, setOrigConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  // Load config
  const loadConfig = useCallback(async () => {
    if (!functionId || !year) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/get_notification_config?function_id=${functionId}&year=${year}`,
        { headers: getAuthHeaders() }
      );
      handleResponse(response);
      const data = await response.json();
      const merged = { ...DEFAULT_CONFIG, ...data };
      setConfig(merged);
      setOrigConfig(JSON.parse(JSON.stringify(merged)));
    } catch (err) {
      setConfig({ ...DEFAULT_CONFIG });
      setOrigConfig(JSON.parse(JSON.stringify(DEFAULT_CONFIG)));
    } finally {
      setLoading(false);
    }
  }, [functionId, year]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const isDirty = config && origConfig && JSON.stringify(config) !== JSON.stringify(origConfig);

  // Save
  const handleSave = async () => {
    setSaving(true);
    setResult(null);
    try {
      const response = await fetch(`${BASE_URL}/save_notification_config`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ function_id: functionId, year, config }),
      });
      handleResponse(response);
      if (!response.ok) throw new Error("Save failed");
      setOrigConfig(JSON.parse(JSON.stringify(config)));
      setResult({ type: "success", message: "Notification settings saved!" });
    } catch (err) {
      setResult({ type: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Test
  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const response = await fetch(`${BASE_URL}/test_notification`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ function_id: functionId, year }),
      });
      handleResponse(response);
      const data = await response.json();
      if (response.ok) {
        setResult({ type: "success", message: "Test notification sent! Check your Slack channel." });
      } else {
        setResult({ type: "error", message: data.error || "Test failed" });
      }
    } catch (err) {
      setResult({ type: "error", message: err.message });
    } finally {
      setTesting(false);
    }
  };

  // Update helpers
  const updateField = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));
  const updateEvent = (key, value) => setConfig((prev) => ({
    ...prev,
    events: { ...prev.events, [key]: value },
  }));
  const updateOverdue = (field, value) => setConfig((prev) => ({
    ...prev,
    overdue_settings: { ...prev.overdue_settings, [field]: value },
  }));

  if (loading) {
    return (
      <div className="admin-tab-content">
        <div className="admin-loading"><div className="admin-loading__spinner" /><p>Loading notification settings...</p></div>
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="admin-tab-content">
      {/* ─── Header actions ─── */}
      <div className="admin-tab-content__toolbar">
        <button
          className="admin-btn admin-btn--outline"
          onClick={handleTest}
          disabled={testing || !config.enabled}
          title={!config.enabled ? "Enable notifications first" : "Send test message"}
        >
          {testing ? <><span className="admin-btn__spinner" style={{ borderTopColor: "var(--accent)" }} /> Testing...</> : <><Send size={13} /> Test</>}
        </button>
        <button
          className={`admin-btn admin-btn--primary ${saving ? "admin-btn--loading" : ""}`}
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? <><span className="admin-btn__spinner" /> Saving...</> : <><CheckCircle size={13} /> Save Settings</>}
        </button>
      </div>

      {/* ─── Result Banner ─── */}
      {result && (
        <div style={{
          display: "flex", alignItems: "center", gap: "8px",
          padding: "10px 14px", borderRadius: "8px", marginBottom: "16px",
          fontSize: "13px", fontWeight: 600,
          background: result.type === "success" ? "color-mix(in srgb, var(--positive) 8%, transparent)" : "color-mix(in srgb, var(--negative) 8%, transparent)",
          color: result.type === "success" ? "var(--positive)" : "var(--negative)",
          border: `1px solid ${result.type === "success" ? "color-mix(in srgb, var(--positive) 25%, transparent)" : "color-mix(in srgb, var(--negative) 25%, transparent)"}`,
        }}>
          {result.type === "success" ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
          <span>{result.message}</span>
          <button onClick={() => setResult(null)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: "16px" }}>✕</button>
        </div>
      )}

      {/* ─── Master Toggle ─── */}
      <CollapsibleSection
        title="Notification Status"
        subtitle={config.enabled ? "Notifications are active" : "Notifications are disabled"}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "8px 0" }}>
          <button
            onClick={() => updateField('enabled', !config.enabled)}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "10px 20px", border: "1px solid var(--border)", borderRadius: "8px",
              background: config.enabled ? "color-mix(in srgb, var(--positive) 8%, transparent)" : "var(--bg-primary)",
              color: config.enabled ? "var(--positive)" : "var(--text-secondary)",
              cursor: "pointer", fontSize: "14px", fontWeight: 600, fontFamily: "inherit",
              transition: "all 0.2s",
            }}
          >
            {config.enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
            {config.enabled ? "Enabled" : "Disabled"}
          </button>
          <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
            {config.enabled
              ? "Slack notifications will fire on configured events"
              : "No notifications will be sent"}
          </span>
        </div>
      </CollapsibleSection>

      {/* ─── Mode Selection ─── */}
      {config.enabled && (
        <CollapsibleSection
          title="Delivery Mode"
          subtitle="Choose how notifications reach your Slack channel"
        >
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
            {/* Webhook Card */}
            <div
              onClick={() => updateField('mode', 'webhook')}
              style={{
                flex: 1, padding: "16px", borderRadius: "10px", cursor: "pointer",
                border: `2px solid ${config.mode === 'webhook' ? 'var(--accent)' : 'var(--border)'}`,
                background: config.mode === 'webhook' ? 'color-mix(in srgb, var(--accent) 5%, transparent)' : 'var(--bg-primary)',
                transition: "all 0.2s",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <Wifi size={18} style={{ color: config.mode === 'webhook' ? 'var(--accent)' : 'var(--text-muted)' }} />
                <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--text-primary)" }}>Webhook</span>
              </div>
              <p style={{ margin: 0, fontSize: "12px", color: "var(--text-muted)", lineHeight: 1.5 }}>
                Simple setup. Paste a Slack incoming webhook URL. No app install needed.
              </p>
            </div>

            {/* Bot Card */}
            <div
              onClick={() => updateField('mode', 'bot')}
              style={{
                flex: 1, padding: "16px", borderRadius: "10px", cursor: "pointer",
                border: `2px solid ${config.mode === 'bot' ? 'var(--accent)' : 'var(--border)'}`,
                background: config.mode === 'bot' ? 'color-mix(in srgb, var(--accent) 5%, transparent)' : 'var(--bg-primary)',
                transition: "all 0.2s",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <Bot size={18} style={{ color: config.mode === 'bot' ? 'var(--accent)' : 'var(--text-muted)' }} />
                <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--text-primary)" }}>Bot (cia_pulse_ai)</span>
              </div>
              <p style={{ margin: 0, fontSize: "12px", color: "var(--text-muted)", lineHeight: 1.5 }}>
                Richer messages. Invite cia_pulse_ai bot to your channel and provide the Channel ID.
              </p>
            </div>
          </div>

          {/* Conditional fields */}
          {config.mode === 'webhook' && (
            <FormField label="Webhook URL" required hint="Get this from Slack: App → Incoming Webhooks → Copy URL">
              <input
                className="admin-input"
                type="url"
                value={config.webhook_url}
                onChange={(e) => updateField('webhook_url', e.target.value)}
                placeholder="https://hooks.slack.com/services/T.../B.../xxxxx"
              />
            </FormField>
          )}

          {config.mode === 'bot' && (
            <FormField label="Channel ID" required hint="Right-click channel → View channel details → Copy Channel ID. Invite @cia_pulse_ai to the channel.">
              <input
                className="admin-input"
                value={config.bot_channel_id}
                onChange={(e) => updateField('bot_channel_id', e.target.value)}
                placeholder="C06XXXXXXX"
              />
            </FormField>
          )}
        </CollapsibleSection>
      )}

      {/* ─── Event Selection ─── */}
      {config.enabled && (
        <CollapsibleSection
          title="Events to Notify"
          subtitle="Choose which actions trigger a Slack notification"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {EVENT_OPTIONS.map(({ key, label, icon, desc }) => (
              <label
                key={key}
                style={{
                  display: "flex", alignItems: "center", gap: "12px",
                  padding: "10px 14px", borderRadius: "8px",
                  border: "1px solid var(--border)",
                  background: config.events[key] ? "color-mix(in srgb, var(--accent) 4%, transparent)" : "var(--bg-primary)",
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                <input
                  type="checkbox"
                  checked={config.events[key] || false}
                  onChange={(e) => updateEvent(key, e.target.checked)}
                  style={{ width: "16px", height: "16px", accentColor: "var(--accent)" }}
                />
                <span style={{ fontSize: "16px" }}>{icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)" }}>{label}</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{desc}</div>
                </div>
              </label>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ─── Overdue Settings ─── */}
      {config.enabled && (config.events.action_overdue || config.events.action_expiring_soon) && (
        <CollapsibleSection
          title="Overdue Alert Settings"
          subtitle="Configure timing for deadline-based notifications"
        >
          <div className="admin-form-grid">
            <FormField label="Warn Days Before ECD" hint="How many days before deadline to send warning">
              <input
                className="admin-input"
                type="number"
                min={1}
                max={14}
                value={config.overdue_settings.warn_days_before}
                onChange={(e) => updateOverdue('warn_days_before', Number(e.target.value))}
              />
            </FormField>
            <FormField label="Alert Frequency" hint="Avoid spamming the same item daily">
              <select
                className="admin-input admin-select"
                value={config.overdue_settings.notify_once_per_item ? "once" : "daily"}
                onChange={(e) => updateOverdue('notify_once_per_item', e.target.value === "once")}
              >
                <option value="once">Once per item (recommended)</option>
                <option value="daily">Every daily scan</option>
              </select>
            </FormField>
          </div>
          <div style={{
            marginTop: "12px", padding: "10px 14px", borderRadius: "8px",
            background: "color-mix(in srgb, var(--accent) 6%, transparent)",
            border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)",
            fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.5,
          }}>
            <strong>ℹ️ How it works:</strong> A daily scheduled scan (CloudWatch EventBridge) checks all action items.
            Items with ECD within <strong>{config.overdue_settings.warn_days_before} day(s)</strong> get a warning.
            Items past ECD get an overdue alert.
          </div>
        </CollapsibleSection>
      )}

      {/* ─── Info when disabled ─── */}
      {!config.enabled && (
        <div className="admin-empty-state">
          <Bell size={40} />
          <h4>Notifications Disabled</h4>
          <p>Enable notifications above to configure Slack alerts for this function.</p>
        </div>
      )}
    </div>
  );
}

export default NotificationsTab;
